import { useEffect, useState, type PropsWithChildren } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarChart3Icon,
  Building2Icon,
  ChevronDownIcon,
  EllipsisIcon,
  Loader2Icon,
  PlusIcon,
  RefreshCcwIcon,
  Settings2Icon,
  UsersIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  AdminCompaniesQueryDTO,
  AdminCompanyWithQuotaDTO,
  CompanyQuotaEventDTO,
  CompanyQuotaEventType,
} from '@shared/schemas/quota';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  MembersManagementHeader,
  MembersManagementPagination,
  MembersManagementPanel,
  MembersManagementTable,
  MembersManagementToolbar,
  MembersToolbarButton,
  type MembersManagementTableState,
  type MembersTableColumn,
} from '@/components/common/members-management';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';
import { type UsageColumn, UsageDataTable } from '@/features/usage/components/UsageDataTable';
import { formatCompactNumber, formatDate, formatDateTime, formatNumber } from '@/features/usage/lib/format';
import {
  addAdminCompanyQuotaExtra,
  createAdminCompany,
  listAdminCompanies,
  listAdminCompanyQuotaEvents,
  recomputeAdminCompanyQuota,
  updateAdminCompanyQuota,
} from './company-api';
import { CompanyUiError, getCompanyErrorMessage } from './company-errors';

/** Style canonique des actions primaires (pas de bouton « primary » bleu). */
const PRIMARY_ACTION_BUTTON_CLASS = 'border-black bg-black text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground';

const companyEventLabels: Record<CompanyQuotaEventType, string> = {
  auto_renewed: 'Renouvellement automatique',
  blocked: 'Quota bloqué',
  extra_credit_added: 'Crédit supplémentaire',
  limit_updated: 'Limite modifiée',
  manual_reset: 'Réinitialisation manuelle',
  quota_configured: 'Quota configuré',
  recomputed: 'Consommation recalculée',
  subscription_type_updated: 'Type d’abonnement modifié',
};

type CompanyStatusFilter = NonNullable<AdminCompaniesQueryDTO['status']>;
type CompanySortPreset = 'blocked-first' | 'consumed-desc' | 'created-desc' | 'members-desc' | 'name-asc' | 'name-desc';

const statusFilterOptions: Array<{ label: string; value: CompanyStatusFilter }> = [
  { label: 'Toutes', value: 'all' },
  { label: 'Actives', value: 'active' },
  { label: 'Bloquées', value: 'blocked' },
];

const sortPresetOptions: Array<{ label: string; value: CompanySortPreset }> = [
  { label: 'Nom (A → Z)', value: 'name-asc' },
  { label: 'Nom (Z → A)', value: 'name-desc' },
  { label: 'Membres (plus nombreux d’abord)', value: 'members-desc' },
  { label: 'Consommation (plus élevée d’abord)', value: 'consumed-desc' },
  { label: 'Création (plus récentes d’abord)', value: 'created-desc' },
  { label: 'Bloquées d’abord', value: 'blocked-first' },
];

const sortPresetParams: Record<CompanySortPreset, Pick<AdminCompaniesQueryDTO, 'sortBy' | 'sortDirection'>> = {
  'blocked-first': { sortBy: 'status', sortDirection: 'desc' },
  'consumed-desc': { sortBy: 'consumed', sortDirection: 'desc' },
  'created-desc': { sortBy: 'createdAt', sortDirection: 'desc' },
  'members-desc': { sortBy: 'members', sortDirection: 'desc' },
  'name-asc': { sortBy: 'name', sortDirection: 'asc' },
  'name-desc': { sortBy: 'name', sortDirection: 'desc' },
};

const pageSizeOptions = [10, 20, 50] as const;

type CompanySortBy = AdminCompaniesQueryDTO['sortBy'];
type CompanySortDirection = AdminCompaniesQueryDTO['sortDirection'];
type CompanyColumnKey = 'company' | 'consumed' | 'createdAt' | 'members' | 'status';

const COMPANIES_TABLE_COLUMNS_STORAGE_KEY = 'admin-companies-table-columns';

const defaultCompanyColumns: Array<UsersTableColumnOption & { key: CompanyColumnKey }> = [
  { key: 'company', label: 'Entreprise', visible: true },
  { key: 'members', label: 'Membres', visible: true },
  { key: 'consumed', label: 'Consommation', visible: true },
  { key: 'createdAt', label: 'Créée le', visible: true },
  { key: 'status', label: 'Statut', visible: true },
];

/** Reprend la persistance du tableau utilisateurs : ordre + visibilité, tolérant aux clés inconnues. */
function readStoredCompanyColumns(): typeof defaultCompanyColumns {
  if (typeof window === 'undefined') return defaultCompanyColumns;

  try {
    const raw = window.localStorage.getItem(COMPANIES_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) return defaultCompanyColumns;

    const parsed = JSON.parse(raw) as Array<{ key?: string; visible?: boolean }>;
    if (!Array.isArray(parsed)) return defaultCompanyColumns;

    const defaults = new Map(defaultCompanyColumns.map((column) => [column.key, column]));
    const restored = parsed.flatMap((column) => {
      const match = column.key ? defaults.get(column.key as CompanyColumnKey) : undefined;
      return match
        ? [{ ...match, visible: typeof column.visible === 'boolean' ? column.visible : match.visible }]
        : [];
    });
    const missing = defaultCompanyColumns.filter(
      (column) => !restored.some((restoredColumn) => restoredColumn.key === column.key),
    );
    const merged = [...restored, ...missing];

    // Au moins une colonne visible, sinon le tableau devient illisible.
    return merged.some((column) => column.visible) ? merged : defaultCompanyColumns;
  } catch {
    return defaultCompanyColumns;
  }
}

function CompanySortHeader({
  active,
  children,
  direction,
  onClick,
}: PropsWithChildren<{
  active: boolean;
  direction: CompanySortDirection;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 font-medium text-foreground transition-colors hover:text-muted-foreground"
    >
      <span>{children}</span>
      {active
        ? direction === 'asc'
          ? <ArrowUpIcon className="size-3.5 text-muted-foreground" />
          : <ArrowDownIcon className="size-3.5 text-muted-foreground" />
        : <ArrowDownIcon className="size-3.5 text-muted-foreground/50" />}
    </button>
  );
}

export function AdminCompaniesPage() {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter>('all');
  const [sortBy, setSortBy] = useState<CompanySortBy>('name');
  const [sortDirection, setSortDirection] = useState<CompanySortDirection>('asc');
  const [tableColumns, setTableColumns] = useState(readStoredCompanyColumns);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(10);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [selected, setSelected] = useState<AdminCompanyWithQuotaDTO | null>(null);
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [extraTokens, setExtraTokens] = useState('');
  const [eventsPage, setEventsPage] = useState(1);
  const [isCreateCompanyDialogOpen, setIsCreateCompanyDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  useEffect(() => {
    try {
      window.localStorage.setItem(COMPANIES_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
    } catch {
      // Stockage indisponible (navigation privée) : la préférence reste en mémoire.
    }
  }, [tableColumns]);

  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const listParams: AdminCompaniesQueryDTO = {
    page,
    pageSize,
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
    status: statusFilter,
  };
  const companiesQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () => listAdminCompanies(listParams),
    queryKey: ['admin', 'companies', 'table', listParams],
  });
  const eventsQuery = useQuery({
    enabled: selected !== null,
    queryFn: () => listAdminCompanyQuotaEvents(selected!.id, eventsPage),
    queryKey: ['admin', 'companies', selected?.id, 'quota-events', eventsPage],
  });

  const companies = companiesQuery.data?.data ?? [];
  const meta = companiesQuery.data?.meta ?? {
    filteredTotal: 0,
    page: 1,
    pageCount: 1,
    pageSize,
    total: 0,
  };

  const refreshCompanies = () => queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
  const createCompanyMutation = useMutation({
    mutationFn: (name: string) => createAdminCompany({ name }),
    onError: (error) => toast.error(getCompanyErrorMessage(error)),
    onSuccess: (company) => {
      toast.success(`Entreprise "${company.name}" créée.`);
      setNewCompanyName('');
      setIsCreateCompanyDialogOpen(false);
      void refreshCompanies();
    },
  });

  function submitCreateCompany() {
    const companyName = newCompanyName.trim();
    if (companyName.length < 2) {
      toast.error('Le nom de l’entreprise doit contenir au moins 2 caractères.');
      return;
    }
    createCompanyMutation.mutate(companyName);
  }

  const configureMutation = useMutation({
    mutationFn: async () => {
      if (!selected) return null;
      const normalized = monthlyLimit.trim();
      const limit = normalized === '' ? null : Number(normalized);
      if (limit !== null && (!Number.isSafeInteger(limit) || limit <= 0)) {
        throw new CompanyUiError('La limite mensuelle doit être un entier positif.');
      }
      return updateAdminCompanyQuota(selected.id, {
        monthlyTokenLimit: limit,
        subscriptionType: 'standard',
      });
    },
    onError: (error) => toast.error(getCompanyErrorMessage(error)),
    onSuccess: () => {
      toast.success('Quota entreprise enregistré.');
      void refreshCompanies();
    },
  });
  const extraMutation = useMutation({
    mutationFn: async () => {
      if (!selected) return null;
      const tokens = Number(extraTokens);
      if (!Number.isSafeInteger(tokens) || tokens <= 0) {
        throw new CompanyUiError('Le crédit supplémentaire doit être un entier positif.');
      }
      return addAdminCompanyQuotaExtra(selected.id, {
        reason: 'Crédit ajouté depuis la console plateforme.',
        tokens,
      });
    },
    onError: (error) => toast.error(getCompanyErrorMessage(error)),
    onSuccess: () => {
      setExtraTokens('');
      toast.success('Tokens supplémentaires ajoutés.');
      void refreshCompanies();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'companies', selected?.id, 'quota-events'] });
    },
  });
  const recomputeMutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new CompanyUiError('Sélectionnez une entreprise.');
      return recomputeAdminCompanyQuota(selected.id);
    },
    onError: (error) => toast.error(getCompanyErrorMessage(error)),
    onSuccess: () => {
      toast.success('Consommation recalculée depuis les logs d’usage.');
      void refreshCompanies();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'companies', selected?.id, 'quota-events'] });
    },
  });

  function openCompany(company: AdminCompanyWithQuotaDTO) {
    setSelected(company);
    setMonthlyLimit(company.quota.config?.monthlyTokenLimit?.toString() ?? '');
    setExtraTokens('');
    setEventsPage(1);
  }

  function resetControls() {
    setSearchValue('');
    setStatusFilter('all');
    setSortBy('name');
    setSortDirection('asc');
    setPage(1);
  }

  const activeFilters = statusFilter !== 'all'
    ? [{
        key: 'status',
        label: statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter,
        onClear: () => {
          setStatusFilter('all');
          setPage(1);
        },
      }]
    : [];

  function toggleSort(nextSortBy: CompanySortBy) {
    setPage(1);
    if (nextSortBy === sortBy) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(nextSortBy);
    // Le nom se lit naturellement de A à Z, les mesures du plus grand au plus petit.
    setSortDirection(nextSortBy === 'name' ? 'asc' : 'desc');
  }

  const companyColumnDefinitions: Record<CompanyColumnKey, MembersTableColumn<AdminCompanyWithQuotaDTO>> = {
    company: {
      key: 'company',
      header: (
        <CompanySortHeader active={sortBy === 'name'} direction={sortDirection} onClick={() => toggleSort('name')}>
          Entreprise
        </CompanySortHeader>
      ),
      renderCell: (company) => (
        <span className="block max-w-[22rem] truncate font-medium">{company.name}</span>
      ),
    },
    members: {
      key: 'members',
      header: (
        <CompanySortHeader active={sortBy === 'members'} direction={sortDirection} onClick={() => toggleSort('members')}>
          Membres
        </CompanySortHeader>
      ),
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      renderCell: (company) => formatNumber(company.userCount),
    },
    consumed: {
      key: 'consumed',
      header: (
        <CompanySortHeader active={sortBy === 'consumed'} direction={sortDirection} onClick={() => toggleSort('consumed')}>
          Consommation
        </CompanySortHeader>
      ),
      headerClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      renderCell: (company) => (
        <span title={`${formatNumber(company.totalTokensConsumed)} tokens`}>
          {formatCompactNumber(company.totalTokensConsumed)} tokens
        </span>
      ),
    },
    createdAt: {
      key: 'createdAt',
      header: (
        <CompanySortHeader active={sortBy === 'createdAt'} direction={sortDirection} onClick={() => toggleSort('createdAt')}>
          Créée le
        </CompanySortHeader>
      ),
      headerClassName: 'text-right',
      cellClassName: 'text-right text-muted-foreground',
      renderCell: (company) => formatDate(company.createdAt),
    },
    status: {
      key: 'status',
      header: (
        <CompanySortHeader active={sortBy === 'status'} direction={sortDirection} onClick={() => toggleSort('status')}>
          Statut
        </CompanySortHeader>
      ),
      renderCell: (company) => (
        <span className={cn(
          'inline-flex rounded-full px-2.5 py-1 text-sm font-medium',
          company.quota.status.blocked
            ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
        )}
        >
          {company.quota.status.blocked ? 'Bloqué' : 'Actif'}
        </span>
      ),
    },
  };

  const companyColumns = tableColumns
    .filter((column) => column.visible)
    .map((column) => companyColumnDefinitions[column.key]);

  function renderCompanyActions(company: AdminCompanyWithQuotaDTO) {
    return (
      <div className="flex items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon-sm"
              className="size-10 border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
              data-testid={`company-actions-${company.id}`}
              aria-label={`Actions pour ${company.name}`}
            >
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Actions entreprise</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  data-testid={`view-company-usage-${company.id}`}
                  to={`/admin/companies/${company.id}/usage`}
                >
                  <BarChart3Icon />
                  Voir l’usage
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  data-testid={`manage-company-members-${company.id}`}
                  to={`/admin/companies/${company.id}/members`}
                >
                  <UsersIcon />
                  Gérer les membres
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`manage-company-${company.id}`}
                onClick={() => openCompany(company)}
              >
                <Settings2Icon />
                Gérer le quota
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  const eventColumns: UsageColumn<CompanyQuotaEventDTO>[] = [
    { key: 'event', header: 'Événement', cell: (event) => companyEventLabels[event.eventType] },
    { key: 'actor', header: 'Acteur', cell: (event) => event.actorName ?? event.actorId ?? 'Système' },
    { key: 'delta', header: 'Variation', align: 'right', cell: (event) => event.tokensDelta === null ? '—' : formatNumber(event.tokensDelta) },
    { key: 'reason', header: 'Motif', cell: (event) => event.reason ?? '—' },
    { key: 'date', header: 'Date', align: 'right', cell: (event) => formatDateTime(event.createdAt) },
  ];

  const isRefreshing = companiesQuery.isFetching && !companiesQuery.isLoading;
  const tableState: MembersManagementTableState = companiesQuery.isError
    ? {
        status: 'error',
        content: (
          <div className="px-6 py-10 text-sm text-destructive">
            {getCompanyErrorMessage(companiesQuery.error)}
          </div>
        ),
      }
    : companiesQuery.isLoading
      ? { status: 'loading', showSkeleton: true }
      : companies.length === 0
        ? {
            status: 'empty',
            content: (
              <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
                <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Building2Icon className="size-6" />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-semibold text-foreground">Aucune entreprise ne correspond à cette vue</h3>
                  <p className="max-w-md text-sm leading-6 text-muted-foreground">
                    Ajustez la recherche, les filtres ou l’ordre d’affichage pour retrouver la bonne entreprise.
                  </p>
                </div>
              </div>
            ),
          }
        : { status: isRefreshing ? 'refreshing' : 'success' };

  // Le tri par en-tête peut sortir des presets : on bascule alors sur « Ordre personnalisé ».
  const currentSortPreset = Object.entries(sortPresetParams)
    .find(([, params]) => params.sortBy === sortBy && params.sortDirection === sortDirection)?.[0] as
      CompanySortPreset | undefined;
  const sortPresetLabel = currentSortPreset
    ? sortPresetOptions.find((option) => option.value === currentSortPreset)?.label ?? 'Ordre personnalisé'
    : 'Ordre personnalisé';

  function resetTableColumns() {
    setTableColumns(defaultCompanyColumns);
  }

  return (
    <section className="space-y-6 text-foreground" data-testid="admin-companies-page">
      <MembersManagementHeader
        title="Entreprises"
        description="Gestion des entreprises, de leurs quotas et de leur usage."
        count={meta.filteredTotal}
        actions={(
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <MembersToolbarButton>
                  Options
                  <ChevronDownIcon data-icon="inline-end" />
                </MembersToolbarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Options de page</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={companiesQuery.isFetching}
                    onClick={() => void companiesQuery.refetch()}
                  >
                    {isRefreshing ? <Loader2Icon className="animate-spin" /> : <RefreshCcwIcon />}
                    Actualiser la liste
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={resetControls}>
                    <Settings2Icon />
                    Réinitialiser recherche et filtres
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              data-testid="admin-create-company-button"
              onClick={() => setIsCreateCompanyDialogOpen(true)}
              type="button"
              variant="outline"
              className={cn(PRIMARY_ACTION_BUTTON_CLASS, 'h-12 px-6 text-[15px] font-medium')}
            >
              <PlusIcon data-icon="inline-start" />
              Créer une entreprise
            </Button>
          </>
        )}
      />

      <MembersManagementPanel
        toolbar={(
          <MembersManagementToolbar
            searchValue={searchValue}
            onSearchChange={(value) => {
              setSearchValue(value);
              setPage(1);
            }}
            searchPlaceholder="Rechercher une entreprise"
            searchInputTestId="companies-search-input"
            filterControl={(
              <MembersToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon data-icon="inline-end" />
              </MembersToolbarButton>
            )}
            sortControl={(
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <MembersToolbarButton className="justify-between xl:min-w-56">
                    {sortPresetLabel}
                    <ChevronDownIcon data-icon="inline-end" />
                  </MembersToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={currentSortPreset ?? ''}
                      onValueChange={(value) => {
                        const params = sortPresetParams[value as CompanySortPreset];
                        setSortBy(params.sortBy);
                        setSortDirection(params.sortDirection);
                        setPage(1);
                      }}
                    >
                      {sortPresetOptions.map((option) => (
                        <DropdownMenuRadioItem key={option.value} value={option.value}>
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            columnsControl={(
              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultCompanyColumns)}
                onReset={resetTableColumns}
              />
            )}
            activeFilters={activeFilters}
            onClearAllFilters={() => {
              setStatusFilter('all');
              setPage(1);
            }}
          />
        )}
        table={(
          <MembersManagementTable
            columns={companyColumns}
            rows={companies}
            getRowKey={(company) => company.id}
            getRowTestId={(company) => `company-row-${company.id}`}
            state={tableState}
            skeletonRowCount={Math.min(pageSize, 4)}
            renderActions={renderCompanyActions}
          />
        )}
        pagination={(
          <MembersManagementPagination
            id="companies-pagination"
            page={meta.page}
            pageCount={meta.pageCount}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            totalLabel={(
              <>
                {meta.filteredTotal === meta.total
                  ? `${formatNumber(meta.total)} entreprise${meta.total > 1 ? 's' : ''}`
                  : `${formatNumber(meta.filteredTotal)} sur ${formatNumber(meta.total)} entreprises`}
              </>
            )}
            onPageChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        )}
      />

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>Affinez la liste des entreprises.</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 px-4 py-2">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Statut</h3>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      statusFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStatusFilter('all');
                setPage(1);
              }}
            >
              Réinitialiser
            </Button>
            <Button type="button" onClick={() => setIsFilterSheetOpen(false)}>
              Fermer
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog
        open={isCreateCompanyDialogOpen}
        onOpenChange={(open) => {
          if (createCompanyMutation.isPending) return;
          setIsCreateCompanyDialogOpen(open);
          if (!open) setNewCompanyName('');
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Créer une entreprise</DialogTitle>
            <DialogDescription>
              Créez une nouvelle entreprise pour rattacher des utilisateurs.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="create-company-name">Nom de l’entreprise</FieldLabel>
            <Input
              disabled={createCompanyMutation.isPending}
              id="create-company-name"
              onChange={(event) => setNewCompanyName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitCreateCompany();
                }
              }}
              placeholder="Ex: Inersio France"
              value={newCompanyName}
            />
          </Field>
          <DialogFooter>
            <Button
              disabled={createCompanyMutation.isPending}
              onClick={() => setIsCreateCompanyDialogOpen(false)}
              type="button"
              variant="outline"
            >
              Annuler
            </Button>
            <Button
              disabled={createCompanyMutation.isPending}
              onClick={submitCreateCompany}
              type="button"
              variant="outline"
              className={PRIMARY_ACTION_BUTTON_CLASS}
            >
              {createCompanyMutation.isPending ? <Loader2Icon className="animate-spin" /> : null}
              Créer l’entreprise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selected?.name ?? 'Entreprise'}</DialogTitle>
            <DialogDescription>Configuration du quota et journal d’audit.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="configuration" className="space-y-5">
            <TabsList>
              <TabsTrigger value="configuration">Configuration</TabsTrigger>
              <TabsTrigger value="events">Événements</TabsTrigger>
            </TabsList>
            <TabsContent value="configuration" className="space-y-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="company-monthly-limit">Limite mensuelle</FieldLabel>
                  <Input
                    id="company-monthly-limit"
                    inputMode="numeric"
                    min={1}
                    onChange={(event) => setMonthlyLimit(event.target.value)}
                    placeholder="Vide = illimité"
                    type="number"
                    value={monthlyLimit}
                  />
                  <FieldDescription>Le type d’abonnement reste Standard. Une valeur vide rend le quota illimité.</FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="company-extra-tokens">Tokens supplémentaires</FieldLabel>
                  <Input
                    id="company-extra-tokens"
                    inputMode="numeric"
                    min={1}
                    onChange={(event) => setExtraTokens(event.target.value)}
                    placeholder="Ex. 250000"
                    type="number"
                    value={extraTokens}
                  />
                  <Button
                    disabled={extraMutation.isPending || extraTokens.length === 0}
                    onClick={() => extraMutation.mutate()}
                    type="button"
                    variant="outline"
                  >
                    Ajouter le crédit
                  </Button>
                </Field>
              </div>
              <div className="flex flex-wrap gap-3 border-t border-border pt-5">
                <Button
                  disabled={configureMutation.isPending}
                  onClick={() => configureMutation.mutate()}
                  type="button"
                  variant="outline"
                  className={PRIMARY_ACTION_BUTTON_CLASS}
                >
                  Enregistrer le quota
                </Button>
                <Button
                  disabled={recomputeMutation.isPending}
                  onClick={() => recomputeMutation.mutate()}
                  type="button"
                  variant="outline"
                >
                  <RefreshCcwIcon className={recomputeMutation.isPending ? 'animate-spin' : undefined} />
                  Recalculer depuis l’usage
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="events">
              <UsageDataTable
                columns={eventColumns}
                rows={eventsQuery.data?.items ?? []}
                getRowKey={(event) => event.id}
                isLoading={eventsQuery.isLoading}
                emptyLabel="Aucun événement quota."
                page={eventsPage}
                pageCount={eventsQuery.data?.meta.pageCount ?? 1}
                pageSize={eventsQuery.data?.meta.pageSize}
                total={eventsQuery.data?.meta.total}
                onPageChange={setEventsPage}
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button onClick={() => setSelected(null)} type="button" variant="ghost">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
